import { Box, Flex, Text, useColorModeValue, BoxProps } from '@chakra-ui/react';
import React from 'react';

type InfoBoxVariant = 'info' | 'success' | 'warning' | 'alert' | 'neutral';

export interface InfoBoxProps extends Omit<BoxProps, 'children' | 'width' | 'padding' | 'boxShadow' | 'borderRadius'> {
  title: string;
  description: string;
  variant?: InfoBoxVariant;
  fontSize?: 'sm' | 'md';
  width?: string | number;
  padding?: string | number;
  boxShadow?: string;
  borderRadius?: string | number;
  /** Icon element rendered alongside the text */
  icon?: React.ReactNode;
  /** Horizontal position of the icon (default: 'left') */
  iconPosition?: 'left' | 'right';
  /** Vertical alignment of the icon (default: 'top') */
  iconAlign?: 'top' | 'center' | 'bottom';
  /** Icon size in px — applied as w/h on the wrapping Box (default: 20) */
  iconSize?: number | string;
}

const variantStyles: Record<
  InfoBoxVariant,
  {
    light: { bg: string; border: string; titleColor: string; descColor: string; iconColor: string };
    dark:  { bg: string; border: string; titleColor: string; descColor: string; iconColor: string };
  }
> = {
  info: {
    light: { bg: 'blue.50',   border: 'blue.200',   titleColor: 'gray.700', descColor: 'gray.500', iconColor: 'blue.500'   },
    dark:  { bg: 'whiteAlpha.50', border: 'blue.700',   titleColor: 'gray.200', descColor: 'gray.400', iconColor: 'blue.300'   },
  },
  success: {
    light: { bg: 'green.50',  border: 'green.200',  titleColor: 'gray.700', descColor: 'gray.500', iconColor: 'green.500'  },
    dark:  { bg: 'whiteAlpha.50', border: 'green.700',  titleColor: 'gray.200', descColor: 'gray.400', iconColor: 'green.300'  },
  },
  warning: {
    light: { bg: 'yellow.50', border: 'yellow.200', titleColor: 'gray.700', descColor: 'gray.500', iconColor: 'yellow.600' },
    dark:  { bg: 'whiteAlpha.50', border: 'yellow.700', titleColor: 'gray.200', descColor: 'gray.400', iconColor: 'yellow.300' },
  },
  alert: {
    light: { bg: 'red.50',    border: 'red.200',    titleColor: 'gray.700', descColor: 'gray.500', iconColor: 'red.500'    },
    dark:  { bg: 'whiteAlpha.50', border: 'red.700',    titleColor: 'gray.200', descColor: 'gray.400', iconColor: 'red.300'    },
  },
  neutral: {
    light: { bg: 'gray.50',   border: 'gray.200',   titleColor: 'gray.700', descColor: 'gray.500', iconColor: 'gray.500'   },
    dark:  { bg: 'whiteAlpha.50', border: 'gray.600',   titleColor: 'gray.200', descColor: 'gray.400', iconColor: 'gray.400'   },
  },
};

export const InfoBox: React.FC<InfoBoxProps> = ({
  title,
  description,
  variant = 'info',
  fontSize = 'sm',
  width = 'full',
  padding = '16px',
  boxShadow = 'none',
  borderRadius = '12px',
  icon,
  iconPosition = 'left',
  iconAlign = 'top',
  iconSize = 20,
  ...boxProps
}) => {
  const isLight = useColorModeValue(true, false);
  const s = isLight ? variantStyles[variant].light : variantStyles[variant].dark;

  const titleSize = fontSize === 'sm' ? 'sm' : 'md';
  const descSize  = fontSize === 'sm' ? 'xs' : 'sm';

  const alignMap: Record<'top' | 'center' | 'bottom', string> = {
    top:    'flex-start',
    center: 'center',
    bottom: 'flex-end',
  };

  const textContent = (
    <Box flex={1} minW={0}>
      <Text fontSize={titleSize} fontWeight="600" color={s.titleColor} mb="4px">
        {title}
      </Text>
      <Text fontSize={descSize} color={s.descColor}>
        {description}
      </Text>
    </Box>
  );

  const iconContent = icon ? (
    <Box
      flexShrink={0}
      color={s.iconColor}
      w={iconSize}
      h={iconSize}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {icon}
    </Box>
  ) : null;

  return (
    <Box
      w={width}
      bg={s.bg}
      border="1px solid"
      borderColor={s.border}
      borderRadius={borderRadius}
      p={padding}
      boxShadow={boxShadow}
      {...boxProps}
    >
      {iconContent ? (
        <Flex
          align={alignMap[iconAlign]}
          gap="12px"
          flexDirection={iconPosition === 'right' ? 'row-reverse' : 'row'}
        >
          {iconContent}
          {textContent}
        </Flex>
      ) : (
        textContent
      )}
    </Box>
  );
};

export default InfoBox;
